import { useCallback, useMemo } from 'react';

import { MaxPicksPerTypeFragment } from '@/api/limits-and-rules/query.generated';
import { useGlobalState } from '@/hooks/use-global-state';
import { EntryPromotionStatus, GameMode, ProjectionType } from '@/types/api.generated';

import { RemainingPicksForTypeFragment } from '../api/query.generated';
import { BetValidationData } from '../api/types';
import { useBetslipStore } from './use-betslip-store';

/**
 * Returns the entry rules. If betslip is empty, it returns the global entry rules.
 */
export const useEntryRules = () => {
    // Always return the max or the min between dynamic and perfect rules. This allows admins to configure maxes and mins separately for each game mode
    // and we'll disabled the game mode for which the limits are breached.

    // initialRules are the rules we get from the global state which are loaded at app start up
    // currentRules are the rules we get from the validation data
    const allEntryRules = useGlobalState(state => state.allEntryRules);
    const gameTypes = allEntryRules.map(rule => rule.gameType);
    const betslip = useBetslipStore(state => state.betslip);
    const validationData = useBetslipStore(state => state.validationData);
    const initialPerfectRules = allEntryRules.find(rule => rule.gameMode === GameMode.Perfect) ?? allEntryRules[0];
    const initialDynamicRules = allEntryRules.find(rule => rule.gameMode === GameMode.Dynamic) ?? allEntryRules[0];
    const validationPerfectRules = validationData[GameMode.Perfect].betValidation;
    const validationDynamicRules = validationData[GameMode.Dynamic].betValidation;

    const validationRules = useMemo(
        () => [validationPerfectRules, validationDynamicRules].filter(Boolean),
        [validationPerfectRules, validationDynamicRules]
    );
    const initialRules = useMemo(
        () => [initialPerfectRules, initialDynamicRules].filter(Boolean),
        [initialPerfectRules, initialDynamicRules]
    );

    const validationRulesMaxes = useMemo(() => getValidationRulesMaxes(validationRules), [validationRules]);

    const promotionsMap = useMemo(() => {
        return {
            [GameMode.Dynamic]: validationData[GameMode.Dynamic].betValidation?.promotions ?? [],
            [GameMode.Perfect]: validationData[GameMode.Perfect].betValidation?.promotions ?? [],
        } as Record<GameMode, EntryPromotionStatus[]>;
    }, [validationData]);

    const maxNumberOfPicks =
        validationRulesMaxes?.maxNumberOfPicks ?? Math.max(...initialRules.map(it => it.maxNumberOfPicks));

    const dynamicMinNumberOfPicks = validationDynamicRules?.minNumberOfPicks ?? initialDynamicRules?.minNumberOfPicks;
    const perfectMinNumberOfPicks = validationPerfectRules?.minNumberOfPicks ?? initialPerfectRules?.minNumberOfPicks;
    const minNumberOfPicksMap = {
        [GameMode.Dynamic]: dynamicMinNumberOfPicks,
        [GameMode.Perfect]: perfectMinNumberOfPicks,
    } as Record<GameMode, number>;

    const minNumberOfTeams =
        validationRulesMaxes?.minNumberOfTeams ?? Math.min(...initialRules.map(it => it.minNumberOfTeams));

    const minNumberOfPicks =
        validationRulesMaxes?.minNumberOfPicks ?? Math.min(...initialRules.map(it => it.minNumberOfPicks));

    const canMakeAdditionalPick = betslip.length < maxNumberOfPicks;

    const remainingPicksForType = useCallback(
        (pickType: ProjectionType) => {
            return (
                findMaxRemainingPicksForType(validationRules.flatMap(it => it.remaining)).find(
                    it => it.type === pickType
                )?.count ?? 0
            );
        },
        [validationRules]
    );

    return {
        /**
         * Indicates if the user can make additional picks
         */
        canMakeAdditionalPick,
        /**
         * Function to get the remaining picks for a certain type based on validation data.
         */
        remainingPicksForType,
        maxNumberOfPicks,
        minNumberOfTeams,
        minNumberOfPicks,
        allEntryRules,
        gameTypes,
        minNumberOfPicksMap,
        promotionsMap,
    };
};

export const getMaxPicksForType = (maxes: MaxPicksPerTypeFragment[], type?: ProjectionType): number => {
    return maxes?.find(it => it?.type === type)?.maxPicks ?? 0;
};

const getValidationRulesMaxes = (currentRules: BetValidationData[]) => {
    if (currentRules.length < 2) {
        return currentRules.length ? currentRules[0] : undefined;
    }
    const perfectMaxNumberOfPicks = currentRules.find(it => it.gameMode === GameMode.Perfect)?.maxNumberOfPicks ?? 0;
    const dynamicMaxNumberOfPicks = currentRules.find(it => it.gameMode === GameMode.Dynamic)?.maxNumberOfPicks ?? 0;

    const maxNumberOfPicksForType = findMaxPicksForType(currentRules.flatMap(it => it.maxNumberOfPicksForType ?? []));

    return {
        maxNumberOfPicks: Math.max(perfectMaxNumberOfPicks, dynamicMaxNumberOfPicks),
        maxNumberOfPicksForType,
        minNumberOfTeams: Math.min(...currentRules.map(it => it.minNumberOfTeams)),
        minNumberOfPicks: Math.min(...currentRules.map(it => it.minNumberOfPicks)),
    };
};

/**
 * Finds the max remaining picks for each type, from a combined list of remaining picks.
 * This is useful when dealing with multiple validation data (dynamic,perfect) and we want to
 * find the maxes out of both validation data
 */
export const findMaxRemainingPicksForType = (
    data: RemainingPicksForTypeFragment[]
): RemainingPicksForTypeFragment[] => {
    return data.reduce<RemainingPicksForTypeFragment[]>((acc, cur) => {
        const existing = acc.find(it => it.type === cur.type);
        if (existing) {
            existing.count = Math.max(existing.count, cur.count);
        } else {
            acc.push(cur);
        }
        return acc;
    }, []);
};

/**
 * Finds the max picks for each type, from a combined list of max picks.
 * This is useful when dealing with multiple entry rules (dynamic,perfect) and we want to
 * find the maxes out of both entry rules
 */
export const findMaxPicksForType = (data: MaxPicksPerTypeFragment[]): MaxPicksPerTypeFragment[] => {
    return data.reduce<MaxPicksPerTypeFragment[]>((acc, cur) => {
        const existing = acc.find(it => it.type === cur.type);
        if (existing) {
            existing.maxPicks = Math.max(existing.maxPicks, cur.maxPicks);
        } else {
            acc.push(cur);
        }
        return acc;
    }, []);
};

/**
 * Function to get the max allowed picks for a certain type based on initial rules.
 */
export const getMaxAllowedPicksForType = (pickType: ProjectionType): number => {
    const allEntryRules = useGlobalState.getState().allEntryRules;
    const initialPerfectRules = allEntryRules.find(rule => rule.gameMode === GameMode.Perfect) ?? allEntryRules[0];
    const initialDynamicRules = allEntryRules.find(rule => rule.gameMode === GameMode.Dynamic) ?? allEntryRules[0];
    const initialRules = [initialPerfectRules, initialDynamicRules].filter(Boolean);
    return (
        findMaxPicksForType(initialRules.flatMap(it => it.maxNumberOfPicksForType)).find(it => it.type === pickType)
            ?.maxPicks ?? 0
    );
};

/**
 * Function to check if the user can add a pick of a certain type.
 * Checks based on the validation rules, otherwise falls back to the initial rules maxes.
 */
export const getCanAddPickOfType = (pickType: ProjectionType): boolean => {
    const currentValidationData = useBetslipStore.getState().validationData;
    const currentValidationRules = [
        currentValidationData[GameMode.Perfect].betValidation,
        currentValidationData[GameMode.Dynamic].betValidation,
    ].filter(Boolean);

    if (currentValidationRules.length > 0) {
        const remaining =
            findMaxRemainingPicksForType(currentValidationRules.flatMap(it => it.remaining)).find(
                it => it.type === pickType
            )?.count ?? 0;
        return remaining > 0;
    }
    return getMaxAllowedPicksForType(pickType) > 0;
};
